import { useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import {
  Building2,
  NotebookIcon,
  Package,
  RouteIcon,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import { TooltipProvider } from 'components/Tooltip';
import ButtonLoader from 'components/loading/ButtonLoader';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { Load, NormalizedLoad } from 'types/Load';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { AvailableTabs } from './constants';
import { useTransportProFormLogic } from './hooks/useTransportProFormLogic';
import { CustomerSection } from './sections/CustomerSection';
import { ItemsSection } from './sections/ItemsSection';
import { NotesSection } from './sections/NotesSection';
import { SpecificationsSection } from './sections/SpecificationsSection';
import { StopsSection } from './sections/StopsSection';

interface UnifiedTransportProLoadFormProps {
  isCreateMode: boolean;
  load?: Load;
  onSubmit?: (data: NormalizedLoad) => void;
  externalLinks?: unknown;
}

type TransportProLoadFormBodyProps = UnifiedTransportProLoadFormProps & {
  load: Load;
};

function TransportProLoadFormBody({
  isCreateMode,
  load,
  onSubmit: _onSubmit,
  externalLinks: _externalLinks,
}: TransportProLoadFormBodyProps) {
  const [activeTabs, setActiveTabs] = useState<string[]>(
    Object.values(AvailableTabs)
  );

  const { formMethods, sortedStops, isLoading, handleFormSubmit } =
    useTransportProFormLogic({
      isCreateMode,
      load,
      activeTabs,
      setActiveTabs,
    });

  return (
    <div className='mb-5'>
      <TooltipProvider>
        <ExtendedFormProvider
          aiDefaultValues={false}
          highlightDirtyFields={!isCreateMode}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={handleFormSubmit}>
              <Accordion
                type='multiple'
                value={activeTabs}
                onValueChange={setActiveTabs}
              >
                <LoadSectionAccordionItem
                  label='Customer'
                  icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.customer}
                  activeTabs={activeTabs}
                >
                  <CustomerSection />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Specs'
                  icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.specifications}
                  activeTabs={activeTabs}
                >
                  <SpecificationsSection formMethods={formMethods} />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Items'
                  icon={<Package className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.items}
                  activeTabs={activeTabs}
                >
                  <ItemsSection formMethods={formMethods} />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Stops'
                  icon={<RouteIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.stops}
                  activeTabs={activeTabs}
                >
                  <StopsSection formMethods={formMethods} stops={sortedStops} />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Notes'
                  icon={<NotebookIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.notes}
                  activeTabs={activeTabs}
                >
                  <NotesSection formMethods={formMethods} loadId={load.ID} />
                </LoadSectionAccordionItem>
              </Accordion>

              <section className='w-full mt-4'>
                <Button
                  buttonNamePosthog={
                    ButtonNamePosthog.UpdateTMSLoadWithAppointment
                  }
                  type='submit'
                  className='w-full'
                  disabled={isLoading.submitting || sortedStops.length === 0}
                >
                  {isLoading.submitting ? (
                    <ButtonLoader />
                  ) : (
                    ButtonText.UpdateTMS
                  )}
                </Button>
              </section>
            </form>
          </FormProvider>
        </ExtendedFormProvider>
      </TooltipProvider>
    </div>
  );
}

export default function UnifiedTransportProLoadForm({
  isCreateMode,
  load,
  onSubmit,
  externalLinks,
}: UnifiedTransportProLoadFormProps) {
  if (isCreateMode || !load) {
    return null;
  }

  return (
    <TransportProLoadFormBody
      isCreateMode={isCreateMode}
      load={load}
      onSubmit={onSubmit}
      externalLinks={externalLinks}
    />
  );
}
